import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { useAuth } from '@/hooks/useAuth'
import AuthLayout from '@/components/auth/AuthLayout'
import SocialButtons from '@/components/auth/SocialButtons'

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
})

type LoginForm = z.infer<typeof loginSchema>

function safeRedirectPath(redirect: string | null): string {
  if (!redirect) return '/dashboard'
  try {
    const url = new URL(redirect, window.location.origin)
    if (url.origin !== window.location.origin) return '/dashboard'
    if (!url.pathname.startsWith('/') || url.pathname.startsWith('//')) return '/dashboard'
    const allowed = url.pathname === '/dashboard' || url.pathname.startsWith('/dashboard/') || url.pathname.startsWith('/teams/invite/accept')
    return allowed ? `${url.pathname}${url.search}` : '/dashboard'
  } catch {
    return '/dashboard'
  }
}

export default function Login() {
  const { t } = useTranslation('auth')
  const { login, isLoading } = useAuth()
  const [searchParams] = useSearchParams()
  const [apiError, setApiError] = useState('')
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginForm) => {
    setApiError('')
    try {
      await login(data.email, data.password)
      const redirect = searchParams.get('redirect')
      window.location.href = safeRedirectPath(redirect)
    } catch (error) {
      console.error('Login failed:', error)
      setApiError(t('login.errors.invalidCredentials'))
    }
  }

  return (
    <AuthLayout>
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl">{t('login.title')}</CardTitle>
          <CardDescription>
            {t('login.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <SocialButtons mode="login" onError={setApiError} />

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                {t('login.divider')}
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email">{t('login.form.email.label')}</label>
              <Input
                id="email"
                type="email"
                placeholder="m@example.com"
                autoComplete="email"
                {...register('email')}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <label htmlFor="password">{t('login.form.password.label')}</label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                {...register('password')}
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>
            {apiError && (
              <p className="text-sm text-destructive">{apiError}</p>
            )}
            <Button type="submit" className="w-full" disabled={isSubmitting || isLoading}>
              {isSubmitting || isLoading ? t('login.buttonLoading') : t('login.button')}
            </Button>
          </form>
          <p className="text-center text-sm text-muted-foreground">
            {t('login.links.noAccount')}{' '}
            <Link to="/register" className="text-primary hover:underline">
              {t('login.links.signUp')}
            </Link>
          </p>
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
